/* Правка текста слайда на итоге без React (план вехи 3, K-SE; ядро use-slide-edit.ts): PATCH /app/orders/{id}/slides/{slide}
   с expected_revision и If-Match — сервер перерисовывает PNG тем же шаблоном без новой генерации и отвечает 202 с operation_order_id,
   подзаказом, у которого после done полный новый результат. Цепочка правок (steos:slides:edit:<owner>:<root>) пишется ДО транспорта:
   ключ идемпотентности и неизвестный исход переживают обновление страницы, новый ключ выдаётся только после известного исхода.
   Одно ядро для родного хука (use-slide-edit.ts) и сервера движка шаблона (features/infog/infog-server.ts): то же хранилище и та же
   цепочка — правка, начатая одной стороной, продолжается другой. */
import { api, endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { SpeechOrder } from '../../api/speech';

export interface EditIntent {
  source: number;
  revision: number;
  slide: number;
  title: string;
  body: string;
  key: string;
  child?: number;
  unknown: boolean;
}
export interface EditChain {
  owner: number;
  root: number;
  head: number | null;
  pending: EditIntent | null;
}
export const slideEditKey = (owner: number, root: number) => `steos:slides:edit:${owner}:${root}`;
const positive = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) > 0;
/** Цепочка правок из хранилища (нет — пустая); чужая или повреждённая — исключение: правка закрыта */
export function readEditChain(owner: number, root: number): EditChain {
  const raw = sessionStorage.getItem(slideEditKey(owner, root));
  if (!raw) return { owner, root, head: null, pending: null };
  const value = JSON.parse(raw) as EditChain,
    p = value.pending;
  if (
    value.owner !== owner ||
    value.root !== root ||
    (value.head !== null && !positive(value.head)) ||
    (p &&
      (!positive(p.source) ||
        !positive(p.revision) ||
        !Number.isSafeInteger(p.slide) ||
        p.slide < 0 ||
        typeof p.title !== 'string' ||
        typeof p.body !== 'string' ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(p.key) ||
        typeof p.unknown !== 'boolean' ||
        (p.child !== undefined && !positive(p.child))))
  )
    throw new Error('slide_edit_intent_invalid');
  return value;
}
const UNCONFIRMED = 'Правка не подтверждена. Продолжите проверку того же запроса.';
/** Текст отказа правки (русская строка; перевод — uiText при показе) */
export const editErrorText = (failure: unknown) =>
  failure instanceof ApiError
    ? failure.status === 412 || failure.code === 'version_conflict'
      ? 'Слайд уже изменён. Мы обновили итог — проверьте текст и сохраните снова.'
      : failure.code === 'slide_text_too_long'
        ? 'Текст не помещается на слайд. Сократите заголовок или текст.'
        : failure.status === 404
          ? 'Этот слайд больше нельзя изменить.'
          : failure.status === 422
            ? 'Проверьте текст слайда: сервер его не принял.'
            : UNCONFIRMED
    : UNCONFIRMED;

export interface SlideEditView {
  /** Цепочка прочитана (null — ещё читается или правка закрыта) */
  chain: EditChain | null;
  /** Последняя подтверждённая правка (подзаказ) или null — исходный заказ */
  head: SpeechOrder | null;
  busy: boolean;
  error: string;
  /** Цепочка в хранилище чужая или повреждена */
  blocked: boolean;
}
export interface SlideEdit {
  view(): SlideEditView;
  /** Прочитать цепочку и подтверждённую правку (GET головы) */
  load(): Promise<void>;
  /** Новая правка слайда: цепочка с ключом — до PATCH; → правка подтверждена (подзаказ готов) */
  save(slide: number, title: string, body: string): Promise<boolean>;
  /** Продолжить неподтверждённую правку тем же ключом */
  resume(): Promise<boolean>;
  /** Итог на экране: подтверждённая правка или исходный заказ */
  shown(): SpeechOrder | null;
  dispose(): void;
}

/** Ядро правки для владельца owner и исходного готового заказа rootId (root() — его последний снимок); onChange — вид изменился */
export function createSlideEdit(o: { owner: number; rootId: number; root(): SpeechOrder | null; onChange(): void }): SlideEdit {
  const { owner, rootId } = o;
  let chain: EditChain | null = null;
  let head: SpeechOrder | null = null;
  let busy = false,
    error = '',
    blocked = false,
    mutex = false,
    alive = true;
  const changed = () => {
    if (alive) o.onChange();
  };
  const loader = new AbortController();
  let poller: { abort: AbortController; timer: ReturnType<typeof setTimeout> | undefined } | null = null;
  /** Ожидание принятой правки — только GET существующего подзаказа; неизвестный PATCH повторяется вручную */
  function watch(next: EditChain | null) {
    if (poller) {
      poller.abort.abort();
      clearTimeout(poller.timer);
      poller = null;
    }
    const p = next?.pending;
    if (!alive || !next || !p?.child) return;
    const mine = { abort: new AbortController(), timer: undefined as ReturnType<typeof setTimeout> | undefined };
    poller = mine;
    const poll = async () => {
      try {
        const status = await settle(next, p, mine.abort.signal);
        if (status === 'queued' || status === 'running') mine.timer = setTimeout(() => void poll(), 2000);
      } catch {
        if (!mine.abort.signal.aborted && alive) {
          error = UNCONFIRMED;
          changed();
        }
      }
    };
    mine.timer = setTimeout(() => void poll(), 1500);
  }
  const persist = (next: EditChain) => {
    sessionStorage.setItem(slideEditKey(next.owner, next.root), JSON.stringify(next));
    if (!alive) return;
    chain = next;
    watch(next);
    changed();
  };
  const shown = () => {
    const root = o.root();
    return head ?? (root?.status === 'done' && root.id === rootId ? root : null);
  };

  async function settle(current: EditChain, p: EditIntent, signal?: AbortSignal) {
    const next = await endpoints.speech.get(p.child!, signal);
    if (signal?.aborted) return 'aborted';
    if (next.status === 'done') {
      if (alive) head = next;
      persist({ ...current, head: next.id, pending: null });
      return 'done';
    }
    if (next.status === 'failed') {
      if (alive) error = 'Не удалось перерисовать слайд. Текст остался прежним.';
      persist({ ...current, pending: null });
      return 'failed';
    }
    return next.status;
  }
  async function run(edit?: { slide: number; title: string; body: string }) {
    if (!chain || blocked || mutex) return false;
    if (!edit && !chain.pending) return false;
    mutex = true;
    busy = true;
    error = '';
    changed();
    let current = chain;
    try {
      if ((await endpoints.me()).user.id !== owner) throw new Error('owner_changed');
      let p = current.pending;
      if (!p) {
        const source = shown();
        if (!source || source.status !== 'done' || !edit) throw new Error('slide_source_unavailable');
        p = { source: source.id, revision: source.result_revision, ...edit, key: uuid(), unknown: false };
        current = { ...current, pending: p };
        persist(current);
      }
      const wasUnknown = p.unknown;
      // Неизвестность фиксируется до транспорта: закрытие окна не разрешает новый ключ.
      p = { ...p, unknown: true };
      current = { ...current, pending: p };
      persist(current);
      if (!p.child) {
        try {
          const response = await api.request<{ operation_order_id: number }>(`/app/orders/${p.source}/slides/${p.slide}`, {
            method: 'PATCH',
            body: { title: p.title, body: p.body, expected_revision: p.revision },
            ifMatch: `"${p.revision}"`,
            idempotency: p.key,
          });
          if (!positive(response.operation_order_id)) throw new Error('invalid_receipt');
          p = { ...p, child: response.operation_order_id };
          current = { ...current, pending: p };
          persist(current);
        } catch (failure) {
          if (!wasUnknown && failure instanceof ApiError && [400, 404, 409, 412, 422].includes(failure.status)) {
            current = { ...current, pending: null };
            persist(current);
            if (failure.status === 412 && current.head) {
              const fresh = await endpoints.speech.get(current.head);
              if (alive && fresh.status === 'done') head = fresh;
            }
          }
          if (alive) error = editErrorText(failure);
          return false;
        }
      }
      return (await settle(current, p)) === 'done';
    } catch {
      if (alive) error = UNCONFIRMED;
      return false;
    } finally {
      mutex = false;
      busy = false;
      changed();
    }
  }

  return {
    view: () => ({ chain, head, busy, error, blocked }),
    async load() {
      try {
        const saved = readEditChain(owner, rootId);
        const current = saved.head ? await endpoints.speech.get(saved.head, loader.signal) : null;
        if (loader.signal.aborted || !alive) return;
        chain = saved;
        head = current?.status === 'done' ? current : null;
        watch(saved);
      } catch {
        if (!loader.signal.aborted && alive) blocked = true;
      }
      changed();
    },
    save: (slide, title, body) => run({ slide, title, body }),
    resume: () => run(),
    shown,
    dispose() {
      alive = false;
      loader.abort();
      watch(null);
    },
  };
}
